
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Mail, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { loginSchema, useStudentAuth } from "@/hooks/useStudentAuth";
import { z } from "zod";
import { useLanguage } from "@/components/LanguageContext";

type StudentLoginFormProps = {
  logoUrl: string;
  onForgotPassword?: () => void;
};

export const StudentLoginForm = ({ logoUrl, onForgotPassword }: StudentLoginFormProps) => {
  const { isSubmitting, handleLogin } = useStudentAuth();
  const { t } = useLanguage();
  
  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof loginSchema>) => {
    await handleLogin(values.email, values.password);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center space-y-4">
        <img 
          src="/lovable-uploads/e79feb48-6e7c-488a-8626-bb79f4bde67a.png" 
          alt="Logo" 
          className="h-20 w-auto"
        />
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight dark:text-white">
            {t('auth.student.login.title')}
          </h1>
          <p className="text-sm text-muted-foreground dark:text-gray-400">
            {t('auth.student.login.subtitle')}
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="dark:text-gray-300">{t('auth.student.login.emailLabel')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      {...field}
                      type="email"
                      className="pl-10 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder={t('auth.student.login.emailPlaceholder')}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="dark:text-gray-300">{t('auth.student.login.passwordLabel')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      {...field}
                      type="password"
                      className="pl-10 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder={t('auth.student.login.passwordPlaceholder')}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {t('auth.student.login.submitButton')}
          </Button>
          
          {onForgotPassword && (
            <div className="text-center mt-2">
              <button
                type="button"
                onClick={onForgotPassword}
                className="text-sm text-primary dark:text-flame hover:underline"
              >
                {t('auth.login.forgotPassword')}
              </button>
            </div>
          )}
        </form>
      </Form>
    </div>
  );
};
